import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { CalendarClock } from "lucide-react";

const SLOTS = ["09:00 AM", "11:00 AM", "01:00 PM", "03:00 PM", "05:00 PM"];
const JS_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const BookingDialog = ({ expert, open, onOpenChange }) => {
  const { user, login } = useAuth();
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const [message, setMessage] = useState("");
  const [phone, setPhone] = useState("");
  const [designation, setDesignation] = useState("");
  const [company, setCompany] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!expert) return null;

  const availability = expert.availability || {};
  const availDays = expert.available_days || Object.keys(availability);
  const selectedWeekday = date ? JS_DAYS[new Date(`${date}T00:00:00`).getDay()] : null;
  const daySlots = selectedWeekday ? (availability[selectedWeekday] || []) : [];
  const dayAvailable = selectedWeekday ? daySlots.length > 0 : true;

  const submit = async () => {
    if (!user) { login(); return; }
    if (!date || !slot) { toast.error("Please pick a date and time slot"); return; }
    if (!dayAvailable) { toast.error(`${expert.name} isn't available on ${selectedWeekday}`); return; }
    if (!phone.trim()) { toast.error("Please enter your phone number so the expert can reach you"); return; }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        expert_id: expert.id, date, time_slot: slot, message,
        customer_phone: phone, customer_designation: designation, customer_company: company,
      });
      toast.success(`Request sent! ${expert.name} will be notified on WhatsApp.`);
      onOpenChange(false);
      setDate(""); setSlot(""); setMessage(""); setPhone(""); setDesignation(""); setCompany("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit booking request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-primary" /> Book {expert.name}
          </DialogTitle>
          <DialogDescription>{expert.current_designation}{expert.company ? ` · ${expert.company}` : ""} · {expert.rate}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="rounded-lg bg-secondary/60 px-3 py-2 text-xs" data-testid="expert-availability">
            <span className="font-semibold">Available days:</span> {availDays.join(", ")}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="booking-date">Preferred date</Label>
            <Input id="booking-date" type="date" value={date} onChange={(e) => { setDate(e.target.value); setSlot(""); }} data-testid="booking-date-input" />
            {date && !dayAvailable && (
              <p className="text-xs text-red-600" data-testid="day-unavailable-msg">{expert.name} is not available on {selectedWeekday}. Please pick another date.</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Time slot</Label>
            <div className="flex flex-wrap gap-2">
              {SLOTS.map((s) => {
                const enabled = daySlots.includes(s) && dayAvailable;
                return (
                <button
                  key={s}
                  type="button"
                  disabled={!enabled}
                  onClick={() => setSlot(s)}
                  data-testid={`slot-${s}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${slot === s ? "bg-primary text-primary-foreground border-primary" : enabled ? "border-border hover:bg-secondary" : "border-border/50 text-muted-foreground/40 line-through cursor-not-allowed"}`}
                >
                  {s}
                </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="booking-phone">Your phone (WhatsApp) *</Label>
            <Input id="booking-phone" placeholder="+91 98765 43210" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="booking-phone-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="booking-designation">Your designation</Label>
              <Input id="booking-designation" placeholder="e.g. Analyst" value={designation} onChange={(e) => setDesignation(e.target.value)} data-testid="booking-designation-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="booking-company">Company</Label>
              <Input id="booking-company" placeholder="e.g. Acme Corp" value={company} onChange={(e) => setCompany(e.target.value)} data-testid="booking-company-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="booking-msg">What's your problem? (optional)</Label>
            <Textarea id="booking-msg" placeholder="Briefly describe what you'd like to discuss…" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="booking-message-input" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={submitting} className="rounded-full w-full font-semibold" data-testid="booking-submit-btn">
            {submitting ? "Sending…" : user ? "Request Consultation" : "Sign in to book"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
